/**
 * Quality gate for the accelerometer stream.
 *
 * Scope: the gate classifies the accelerometer stream it is GIVEN — the
 * whole session in the main processing path, but a single lap where the
 * caller processes one lap's telemetry (`lapMapper.lapDtoToLap`, and mobile
 * `useSessionComparison`). It reaches no further than its inputs. The
 * minimum-evidence rule below is what keeps that safe: a slice too short to
 * judge returns `usable` by design, so a per-lap call can never flip a lap
 * to GPS-derived on thin evidence while the session around it stays fused.
 *
 * Mobile GoPro uploads made before the window-averaging fix reduced the
 * camera's 200 Hz ACCL stream to the 10 Hz GPS rate by PICKING one sample per
 * GPS tick. That aliases motorcycle chassis vibration — which the
 * accelerometer genuinely sees and GPS cannot — straight into the stored
 * signal. The damage is not recoverable after the fact: the discarded 19 of
 * every 20 samples are gone. Such a stream reads a correct 9.84 m/s² at
 * standstill but a median |a| of 15–19 m/s² while riding (p95 ~46–52, max
 * 113), and inflates displayed braking g by 2–4× on every lap.
 *
 * When this gate fires, `processRawTelemetry` drops the whole IMU, gyroscope
 * included (the same per-tick pick aliased both streams), and processes the
 * session like a GPS-only device: GPS lean, and the GPS-derived acceleration
 * the display layer already presents honestly (dashed strokes, "· GPS"
 * legend, "(GPS)" tooltip suffix). This file only DECIDES; it never alters
 * or repairs a stream.
 *
 * The metric is measured on the stored stream REDUCED AS THE FUSION REDUCES
 * IT — the accelerometer reduced to GPS rate by `averageReadingsInWindow`, the same
 * helper `sensorFusionAccelerationCalculator` uses. That matters: a
 * correctly-ingested full-rate stream carries just as much raw vibration, but
 * averaging cancels it (its mean is ~0), so only streams whose vibration was
 * already baked in by single-sample picking survive the reduction and trip
 * the gate.
 *
 * This module is the PURE half: thresholds, the jerk series, and
 * `classifyReducedSamples`, which judges already-reduced samples. The
 * reduction itself — and the `classifyImuVibration` wrapper that the
 * telemetry processor calls — live in `processors/gpsRateAccelReduction.ts`,
 * because they depend on the fusion helper and `analysis/config/` does not
 * import processors.
 *
 * Unit convention: a reading whose `unit` is `'g'` is multiplied by 9.81 and
 * anything else is taken as already m/s², matching the accel calculators
 * (`sensorFusionAccelerationCalculator`, `gpsCoordinatedLean`). Note this is
 * deliberately the INVERSE of `sensorFrameCalibration`'s convention, which
 * works in g and divides; the two are not interchangeable.
 *
 * Metric: p95 of the VECTOR jerk |Δa⃗|/Δt (m/s³) between consecutive reduced
 * samples, over moving samples only. Vector rather than change-in-magnitude
 * because aliased vibration rotates the acceleration vector as much as it
 * scales it. Moving-only because standstill data is clean even in a corrupted
 * session (the engine is idling, the chassis is still), so pit time would
 * dilute the metric toward "usable".
 *
 * Tuning: edit → `npm run build` in src/PitBrain.Shared → restart the mobile
 * bundler / rebuild the web app. Re-measure against real sessions before
 * moving IMU_VIBRATION_MAX_P95_JERK_MS3 — the calibration table below is the
 * evidence the current value rests on.
 */

/**
 * Unit: km/h. Reduced samples at or below this speed are excluded from the
 * metric. Standstill and pit-lane data is clean even in a fully corrupted
 * session — vibration scales with road speed and engine load — so including
 * it drags the p95 down and can hide a corrupted session that happened to
 * spend a long time parked. Default: 30 (comfortably above pit-lane speeds,
 * comfortably below any on-track section).
 */
export const IMU_VIBRATION_MIN_SPEED_KMH = 30

/**
 * Unit: seconds. Consecutive-sample pairs separated by more than this are
 * skipped. A gap that long means a sensor dropout or a session pause, where
 * a large |Δa⃗| says nothing about vibration. Default: 0.5 (the same dropout
 * guard the sensor-fusion gyro integrator uses).
 */
export const IMU_VIBRATION_MAX_SAMPLE_GAP_S = 0.5

/**
 * Unit: count. Minimum moving reduced samples before the verdict is trusted.
 * Below this the session is short, slow or mostly parked, and a p95 from a
 * handful of samples is noise — the conservative answer is to change nothing.
 * Default: 100 (10 s of riding at 10 Hz).
 */
export const IMU_VIBRATION_MIN_MOVING_SAMPLES = 100

/**
 * Unit: m/s³. A session whose p95 vector jerk exceeds this is treated as
 * vibration-corrupted and its IMU is dropped.
 *
 * Calibration (measured 2026-08-31 with this exact metric; see the four
 * mobile-uploaded GoPro sessions from the 2026-07-23 Gellerasen track day):
 *
 *   CORRUPTED (must trip)
 *     559dd242  p95 528   (p50 176, moving samples 19 942)
 *     d24f2040  p95 562   (p50 198, moving samples 17 817)
 *     96347eaa  p95 568   (p50 197, moving samples 20 508)
 *     8844beeb  p95 592   (p50 199, moving samples  6 301) — least noisy by
 *               raw |a| (whole-session p50 9.86, because most of it is
 *               parked), yet the highest jerk once parked time is excluded
 *     d24f2040 60 s slice (the committed test fixture)  p95 454
 *
 *   MUST NOT TRIP
 *     Real Qstarz 10 Hz motorcycle session (QstarzTestFile.csv, Gellerasen,
 *       6 348 moving samples)                            p95  71
 *     Real RaceBox 25 Hz motorcycle session, unit 'G'
 *       (23591450-c437-40d7-9843-54b43d09e916,
 *       "Racebox_20250721_143033", recorded 2025-07-21,
 *       2 306 samples, 2 298 moving)                     p95  69
 *     Synthetic clean equal-rate ramps, 10 Hz and 25 Hz   p95  10
 *     Synthetic 200 Hz stream + ±15 m/s² vibration, after
 *       the window-averaging reduction                    p95  11
 *
 * Rate-independence (measured 2026-09-01, the reason this stays a jerk and
 * not a per-sample delta): a per-sample |Δa⃗| limit would have to be
 * rescaled for every device rate, and dividing by Δt is what makes one
 * number serve both. The two REAL clean sessions confirm it empirically —
 * Qstarz at 10 Hz reads p95 71, RaceBox at 25 Hz reads p95 69, within 3% of
 * each other, even though their per-sample deltas differ 2.5× (p95 |Δa⃗|
 * 7.1 vs 2.86 m/s²). Genuine vehicle acceleration simply changes less
 * between samples taken closer together, so the ratio holds. Extrapolating
 * the Qstarz per-sample delta to 25 Hz — which predicted ~178 and looked
 * alarming — assumed the opposite and was wrong.
 *
 * Default: 200 — roughly the geometric mean of the two populations: 2.8×
 * above the noisiest genuine stream measured (Qstarz 71, RaceBox 69) and
 * 2.3× below the quietest corrupted evidence (the 454 fixture slice). Lower
 * it only with a real session between 71 and 200 that is provably corrupted;
 * raise it if a genuine device is ever found above 200.
 *
 * Note: expressed as VECTOR jerk, these numbers run ~4× the change-in-
 * magnitude figures quoted in the original investigation (|Δ|a|| p95 13–20
 * m/s² ≈ 140 m/s³ at 10 Hz) — the two metrics are not interchangeable.
 */
export const IMU_VIBRATION_MAX_P95_JERK_MS3 = 200

/** One accelerometer sample reduced to a GPS tick, in m/s², with that tick's speed. */
export interface GpsRateAccelSample {
  x: number
  y: number
  z: number
  timestampMs: number
  speedKmh: number
}

export type ImuVibrationResult = {
  /** p95 vector jerk over moving pairs (m/s³); NaN when no pair qualified. */
  p95JerkMs3: number
  /** Reduced samples above IMU_VIBRATION_MIN_SPEED_KMH. */
  movingSampleCount: number
} & (
  | { verdict: 'usable'; reason: 'insufficient-evidence' | 'within-limits' }
  | { verdict: 'vibration-corrupted' }
)

/**
 * Vector jerk |Δa⃗|/Δt (m/s³) for every consecutive pair where both samples
 * are moving and the gap is usable. Order follows the input.
 */
export function movingJerkSeriesMs3(samples: GpsRateAccelSample[]): number[] {
  const jerks: number[] = []

  for (let i = 1; i < samples.length; i++) {
    const previous = samples[i - 1]
    const current = samples[i]
    if (
      previous.speedKmh <= IMU_VIBRATION_MIN_SPEED_KMH ||
      current.speedKmh <= IMU_VIBRATION_MIN_SPEED_KMH
    ) {
      continue
    }

    const dtSeconds = (current.timestampMs - previous.timestampMs) / 1000
    if (dtSeconds <= 0 || dtSeconds > IMU_VIBRATION_MAX_SAMPLE_GAP_S) continue

    const dx = current.x - previous.x
    const dy = current.y - previous.y
    const dz = current.z - previous.z
    jerks.push(Math.sqrt(dx * dx + dy * dy + dz * dz) / dtSeconds)
  }

  return jerks
}

/** Nearest-rank percentile of an ASCENDING-sorted series. NaN when empty. */
export function percentileOfSorted(
  sortedAscending: number[],
  fraction: number
): number {
  if (sortedAscending.length === 0) return NaN
  const index = Math.round(fraction * (sortedAscending.length - 1))
  return sortedAscending[
    Math.min(sortedAscending.length - 1, Math.max(0, index))
  ]
}

/**
 * Decide whether an accelerometer stream, ALREADY reduced to GPS rate the
 * way the fusion will consume it, can be trusted:
 *  1. Fewer than IMU_VIBRATION_MIN_MOVING_SAMPLES moving samples → usable
 *     (never change behaviour on thin evidence).
 *  2. p95 vector jerk above IMU_VIBRATION_MAX_P95_JERK_MS3 →
 *     vibration-corrupted.
 *  3. Otherwise → usable.
 *
 * `processors/gpsRateAccelReduction.classifyImuVibration` is the wrapper
 * that does the reduction and calls this; callers normally want that one.
 */
export function classifyReducedSamples(
  samples: GpsRateAccelSample[]
): ImuVibrationResult {
  const movingSampleCount = samples.filter(
    (s) => s.speedKmh > IMU_VIBRATION_MIN_SPEED_KMH
  ).length

  if (movingSampleCount < IMU_VIBRATION_MIN_MOVING_SAMPLES) {
    return {
      verdict: 'usable',
      reason: 'insufficient-evidence',
      p95JerkMs3: NaN,
      movingSampleCount,
    }
  }

  const jerks = movingJerkSeriesMs3(samples).sort((a, b) => a - b)
  const p95JerkMs3 = percentileOfSorted(jerks, 0.95)

  if (p95JerkMs3 > IMU_VIBRATION_MAX_P95_JERK_MS3) {
    return { verdict: 'vibration-corrupted', p95JerkMs3, movingSampleCount }
  }

  return {
    verdict: 'usable',
    reason: 'within-limits',
    p95JerkMs3,
    movingSampleCount,
  }
}
